import { Box } from '@mui/material';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';

import ContentNotification from '@/components/ContentNotfication';
import ResizablePanel from '@/components/ResizablePanel';
import CloudSourcesTable from '@/features/CloudSourcesTable/CloudSourcesTable';
import type { CloudSourcesTableProps } from '@/features/CloudSourcesTable/CloudSourcesTable.types';
import { useFetchDeviceType } from '@/features/DeviceCreate/DeviceCreate.hooks';
import { useSourceDialog } from '@/features/SourceCreate/SourceCreate.hooks';
import SourceCreateDialog from '@/features/SourceCreate/SourceCreateDialog';
import useDeviceQueryParam, {
  CLOUD_SOURCE_DETAILS_PAGE,
  useDeviceSelectionParams
} from '@/pages/Administration/Devices/Devices.hooks';
import { isCustomerCloudSourcePageAtom } from '@/pages/Administration/Devices/Devices.store';
import { selectedOrganizationIdAtom } from '@/pages/Organizations';

import useFetchEnvironment from '../../Administration.hooks';
import { showAlertAtom } from '../../Administration.store';
import DeviceDetails from '../DeviceDetails';
import useStyles from '../Devices.styles';
import useCustomNotification, {
  type CustomNotificationProps
} from '../SyncDevices/useCustomNotification';

const CloudSources = ({ isCustomerCloudSource }: CloudSourcesTableProps) => {
  const { hasDetailSelection } = useDeviceSelectionParams();
  const { classes } = useStyles({
    deviceDetailsCollapsed: !hasDetailSelection
  });
  const { open: showSourcePopup } = useSourceDialog();
  const [, setCustomerCloudSourcePage] = useAtom(isCustomerCloudSourcePageAtom);
  const [selectedOrganizationId] = useAtom(selectedOrganizationIdAtom);
  //-----------------------------------------------------------------

  useFetchEnvironment();
  useFetchDeviceType();
  const { removeFilter, updateSearchParams } = useDeviceQueryParam();
  const [showAlert] = useAtom(showAlertAtom);
  const {
    ConfirmationModal: CustomNotificationModal,
    confirm: ShowCustomNotification
  } = useCustomNotification({
    title: 'Devices',
    message:
      'CRUD operations are allowed only for the following organizations in the QA and Staging environments: "Test Pondurance", "Pondurance Demo-1", "Pondurance Demo-2", and "Marquardt LLC"',
    severity: 'info',
    buttonType: 'Close'
  } as CustomNotificationProps);

  const closeDeviceDetail = useCallback(() => {
    removeFilter();
  }, [removeFilter]);

  useEffect(() => {
    if (showAlert) {
      ShowCustomNotification();
    }
  }, [showAlert]);

  useEffect(() => {
    updateSearchParams({ pg: CLOUD_SOURCE_DETAILS_PAGE }, { replace: true });
  }, [updateSearchParams]);

  useEffect(() => {
    setCustomerCloudSourcePage(isCustomerCloudSource);
  }, [isCustomerCloudSource]);

  const cloudSourcesAGGridTableContent = useMemo(
    () => (
      <CloudSourcesTable
        closeDeviceDetail={closeDeviceDetail}
        detailPanelVisible={hasDetailSelection}
        isMainDevicePage={!isCustomerCloudSource}
      />
    ),
    [closeDeviceDetail, hasDetailSelection, isCustomerCloudSource]
  );

  const deviceDetailsContent = useMemo(
    () => (
      <DeviceDetails
        closeDeviceDetail={closeDeviceDetail}
        isMainDevicePage={!isCustomerCloudSource}
      />
    ),
    [closeDeviceDetail, isCustomerCloudSource]
  );

  const panels = useMemo(
    () => [
      {
        content: cloudSourcesAGGridTableContent,
        id: 'devices-table',
        order: 1,
        minSize: hasDetailSelection ? '40%' : '100%',
        maxSize: hasDetailSelection ? '80%' : '100%',
        showResizeHandle: true // Always show handle since we always have 2 panels
      },
      {
        content: deviceDetailsContent,
        id: 'device-details',
        order: 2,
        minSize: hasDetailSelection ? '20%' : '0%',
        maxSize: hasDetailSelection ? '40%' : '0%',
        showResizeHandle: false
      }
    ],
    [hasDetailSelection, cloudSourcesAGGridTableContent, deviceDetailsContent]
  );

  const sizes = ['75%', '25%'];

  // Internal users can view all sources, but for customer cloud source page, only show when an org is selected (org id > 0)
  const shouldRenderCloudSourcesListing =
    typeof selectedOrganizationId === 'number' &&
    (!isCustomerCloudSource || selectedOrganizationId !== 0);

  return (
    <>
      {selectedOrganizationId === 0 && isCustomerCloudSource && (
        <ContentNotification
          title="Select an organization"
          text="Please select an organization to view and edit its cloud sources."
        />
      )}
      {shouldRenderCloudSourcesListing && (
        <Box className={classes.resizablePanelHeighFix}>
          <ResizablePanel sizes={sizes} panels={panels} />
        </Box>
      )}
      {showSourcePopup && (
        <SourceCreateDialog isCustomerCloudSourcePage={isCustomerCloudSource} />
      )}
      <CustomNotificationModal />
    </>
  );
};

export default CloudSources;
